import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { DealsQuestionnaireData, Score } from "@/pages/deals/types";
import { InfoCircle, Loader, Mandatory } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ScoreType, ScoreConfig } from "@/pages/deals/scoreConfig";
import GeneralQuestionnaire from "./GeneralQuestionnaire";
import MappingQuestionnaire from "./MappingQuestionnaire";
import SimpleQuestionnaire from "./SimpleQuestionnaire";
import POCQuestionnaire from "./POCQuestionnaire";
import { DealScoreBadge } from "@/pages/deals/components/DealScoreBadge";
import IconContainer from "@/components/ui/icon-container";
import { isNull } from "lodash";
import { showQuestionnaireScoreToast } from "@/pages/deals/components/DealStages/questionnaireHelpers";

interface Props {
  dealId: string;
  questionnaire: DealsQuestionnaireData;
  openQuestionnaireId?: string;
  onQuestionnaireOpened?: () => void;
  pocQuestionnaire?: DealsQuestionnaireData;
  relatedQuestionnaire?: DealsQuestionnaireData;
  handleRefetch?: () => Promise<void>;
  isLoadingScore?: boolean;
}

export enum QuestionnaireType {
  SIMPLE = "simple",
  MAPPING = "mapping",
  POC = "poc",
  GENERAL = "general",
  SCORE = "score",
  APPROVAL = "approval",
  CUSTOM = "custom" // Not supported right now
}

const Questionnaire: React.FC<Props> = ({
  dealId,
  questionnaire,
  openQuestionnaireId,
  onQuestionnaireOpened,
  pocQuestionnaire,
  relatedQuestionnaire,
  handleRefetch,
  isLoadingScore = false
}) => {
  const { t } = useTranslation();
  const { typeValue, name, questions, score, responses, id, kpiValue } =
    questionnaire;
  const [isOpen, setIsOpen] = useState(false);
  const [localScore, setLocalScore] = useState<Score | null>(null);
  const hasHandledOpen = useRef(false);

  useEffect(() => {
    setLocalScore(score || null);
  }, [score]);

  useEffect(() => {
    if (openQuestionnaireId === id && !hasHandledOpen.current) {
      hasHandledOpen.current = true;
      setIsOpen(true);
      onQuestionnaireOpened?.();
    }
  }, [openQuestionnaireId, id]);

  // Reset when openQuestionnaireId changes to a different ID or clears
  useEffect(() => {
    if (openQuestionnaireId !== id) {
      hasHandledOpen.current = false;
    }
  }, [openQuestionnaireId, id]);

  const hasAnyMandatory = questions?.some((question) => question.isMandatory);

  const isNotCompleted =
    localScore?.scoreText?.toLowerCase() === ScoreConfig.PENDING &&
    isNull(localScore.scoreResult);
  const isStakeholdersApproval = kpiValue === ScoreType.STAKEHOLDERS_APPROVAL;
  const isGeneral = kpiValue === ScoreType.GENERAL;

  const handleUpdateQuestionnaireScore = (
    newScore: Score | null,
    hideErrorToast: boolean = false
  ) => {
    showQuestionnaireScoreToast({
      previousScore: localScore,
      newScore,
      questionnaireName: name,
      questionnaireId: id,
      kpiValue: kpiValue || "",
      hideErrorToast,
      t
    });

    setLocalScore(newScore);
  };

  const renderQuestionnaireName = () => {
    if (isStakeholdersApproval && pocQuestionnaire?.name) {
      return t("deals.dealView.stakeholdersApprovalName", {
        name,
        pocName: pocQuestionnaire?.name
      });
    }
    return name;
  };

  const renderScore = () => {
    if (isLoadingScore) {
      return (
        <IconContainer size="xs" color="primary" nativeStroke>
          <Loader />
        </IconContainer>
      );
    }
    if (isNotCompleted && !isGeneral) {
      return (
        <div className="text-charts-negative text-base">
          {t(
            `deals.dealView.${isStakeholdersApproval ? "stakeholdersApprovalAddScore" : "addScore"}`
          )}
        </div>
      );
    }
    return (
      <DealScoreBadge
        scoreText={localScore?.scoreText || ""}
        scoreType={kpiValue as ScoreType}
      />
    );
  };

  const renderContent = () => {
    return (
      <div className="pt-5">
        {(() => {
          switch (typeValue) {
            case QuestionnaireType.MAPPING:
              return (
                <MappingQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={(newScore) =>
                    handleUpdateQuestionnaireScore(newScore, true)
                  }
                />
              );
            case QuestionnaireType.POC:
              return (
                <POCQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={
                    handleUpdateQuestionnaireScore
                  }
                />
              );
            case QuestionnaireType.GENERAL:
              return (
                <GeneralQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={(newScore) =>
                    handleUpdateQuestionnaireScore(newScore, true)
                  }
                  handleRefetch={handleRefetch}
                />
              );
            case QuestionnaireType.SIMPLE:
            default:
              return (
                <SimpleQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={
                    handleUpdateQuestionnaireScore
                  }
                  relatedQuestionnaire={relatedQuestionnaire}
                  handleRefetch={handleRefetch}
                />
              );
          }
        })()}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full rounded-lg bg-border-widget-graph px-4 py-3 relative">
      <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg bg-surface-tooltip-toast" />

      <div className="flex items-center justify-between">
        <div
          className="flex gap-2 items-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="size-6 rounded-full flex items-center justify-center">
            <ChevronDown
              className={cn(
                "cursor-pointer size-4 text-secondary transition-transform duration-200",
                !isOpen && "-rotate-90"
              )}
            />
          </div>
          <Text variant="label">{renderQuestionnaireName()}</Text>
          {renderScore()}
        </div>
        {hasAnyMandatory && isOpen && kpiValue !== ScoreType.POC && (
          <div className="flex items-center gap-[6px]">
            {isGeneral ? (
              <Text variant="lgLabel">*</Text>
            ) : (
              // Unchanged: the previous value named no token that exists, so no colour was ever applied here.
              <IconContainer className="size-[10px]" color="current">
                <Mandatory />
              </IconContainer>
            )}

            <Text variant="smBodySecondary">
              {isGeneral
                ? t("deals.dealView.mandatoryField")
                : t("deals.dealView.mandatoryCriteria")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="p-[2px] hover:bg-white-8 transition-colors duration-200 ease-in-out rounded size-4 flex items-center justify-center text-tertiary">
                  <InfoCircle />
                </div>
              </TooltipTrigger>

              <TooltipContent maxWidth={18}>
                {t(
                  `deals.dealView.${isGeneral ? "mandatoryFieldTooltip" : "mandatoryCriteriaTooltip"}`
                )}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">{renderContent()}</div>
      </div>
    </div>
  );
};

export default Questionnaire;
